import { Button, FileButton, Stack, Text } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import CompanyFilesLayout from './CompanyFilesLayout';
import { useCompanyFileActions } from '../hooks/useCompanyFileActions';

export default function AddCompanyFilesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const parentId = searchParams.get('parentId') ?? undefined;
  const { uploadFiles } = useCompanyFileActions();
  const [isUploading, setIsUploading] = useState(false);

  const returnPath = parentId ? `/company/documents/folder/${parentId}` : '/company/documents';

  const handleFilesSelected = async (files: File[] | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      await uploadFiles(files.map(file => ({ file, parentId })));
      navigate(returnPath);
    } catch (uploadError) {
      notifyAndLogError(
        'Upload failed',
        'An error occurred while uploading. Please try again or contact support.',
        uploadError,
        { fileCount: files.length, parentId },
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <CompanyFilesLayout title="Upload company documents">
      <Stack gap="md" align="flex-start">
        <Text c="gray.7">
          Choose files to add to the company file store. These are visible to everyone in your firm.
        </Text>
        <FileButton onChange={handleFilesSelected} multiple>
          {props => (
            <Button
              {...props}
              variant="primary"
              size="md"
              leftSection={<IconUpload size={16} />}
              loading={isUploading}
            >
              Choose files
            </Button>
          )}
        </FileButton>
        <Button variant="subtle" onClick={() => navigate(returnPath)} disabled={isUploading}>
          Cancel
        </Button>
      </Stack>
    </CompanyFilesLayout>
  );
}
